import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createVNode as _createVNode, resolveComponent as _resolveComponent, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "task-container w-full" }
const _hoisted_2 = { class: "es-task-box row gap-06 w-full" }
const _hoisted_3 = { class: "font-m" }
const _hoisted_4 = ["src"]
const _hoisted_5 = {
  key: 1,
  class: "font-m mleft-04"
}
const _hoisted_6 = ["src"]
const _hoisted_7 = { class: "font-m mleft-04" }
const _hoisted_8 = {
  key: 0,
  class: "row w-full mtop-15"
}
const _hoisted_9 = {
  key: 1,
  class: "col gap-04 w-full"
}
const _hoisted_10 = { class: "w-full mright-04" }
const _hoisted_11 = {
  key: 2,
  class: "row w-full mtop-15"
}

import { useI18n } from "vue-i18n";
import { ItemSelect } from '@/models/share.model';
import { ref, onMounted, computed, watch } from 'vue';
import { ApiError, TaskBoxLightResponse, AddOrUpdateFinancialChannelRequest, TaskBoxPersonsLightResponse, PersonApi } from '@/apiGenerate/medserver';
import { medServerApiConfig } from '@/config/medserver-api.config';
import { useAppUserStore } from '@/stores/appUser';
import { toastError, toastSuccess } from '@/utils/toast-utils';
import { useToast } from 'primevue';
import SharedService from "@/services/SharedService";
import Select from 'primevue/select';
import EsTaskBoxForm from '@/components/form/task/esTaskBoxForm.vue';
import ToggleSwitch from 'primevue/toggleswitch';


export default /*@__PURE__*/_defineComponent({
  __name: 'esTaskSettings',
  setup(__props) {

const personApi = new PersonApi(medServerApiConfig);

const { t } = useI18n();
const toast = useToast();
const appUserStore = useAppUserStore();

const isEsTaskBoxFormOpened = ref<boolean>(false);
const headerEsTaskBoxForm = ref<string>(t('settings.label.task.taskbox.add.header'));
const taskBoxs = ref<TaskBoxLightResponse[]>([]);
const taskBoxsItemSelect = ref<ItemSelect[]>([]);
const selectedTaskBox = ref<ItemSelect>();
const selectedTaskBoxResponse = ref<TaskBoxLightResponse>(null);
const loadingPermission = ref<boolean>(false);
const taskboxPermissions = ref<TaskBoxPersonsLightResponse[]>([]);

const currentPersonId = computed(() => {
  if (!appUserStore.currentAppUserData || !appUserStore.currentAppUserData.healthGroupMemberships) {return null}
  return appUserStore.currentAppUserData.healthGroupMemberships[0].pwr.personWithRoleId
});

const currInstituteUuid = computed(() => {
  if (!appUserStore.currentHealthGroupMembership) {return "";}
  const currentIwr = appUserStore.currentHealthGroupMembership.instituteWithRole;
  return currentIwr.instituteWithRoleId;
});

const isUpdateDisabled = computed(() => {
  if (!selectedTaskBox.value) return true;
  const selected = taskBoxs.value.find(tb => tb.id.toString() === selectedTaskBox.value?.id);
  return selected?.title === 'Results';
})


/**
 * Transforms a TaskBoxLightResponse object into an ItemSelect object
 * for use in dropdowns or selection components.
 *
 * @param {TaskBoxLightResponse} dataToTransform - The task box data from API.
 * @returns {ItemSelect} The transformed item with id, label, and icon.
 */
const transformToItemSelect = (dataToTransform: TaskBoxLightResponse): ItemSelect => {
    return {
        id: dataToTransform.id.toString(),
        label: dataToTransform.title,
        icon: dataToTransform.moduleType.toLowerCase(),
        iconFolder: "task"
    }
};

/**
 * Fetches all task boxes for the current person and updates the
 * reactive state `taskBoxs` and `taskBoxsItemSelect`.
 */
const getTaskBoxs = () => {
  personApi.findTaskBoxByPerson({personId: currentPersonId.value}).then((result: TaskBoxLightResponse[]) => {
      taskBoxs.value = result;
      taskBoxsItemSelect.value = taskBoxs.value.map((taskBox: TaskBoxLightResponse) => transformToItemSelect(taskBox));
  }).catch((error: ApiError) => {
      toastError(toast, {
          summary: t('common.toast.error'),
          detail: error.status ? t(`error.${error.status}`) : t('error.DEFAULT')
      });
  });
};

/**
 * Opens the update form for the currently selected task box.
 * It finds the selected task box details, maps API enum values
 * if needed, and sets the modal header for update mode.
 */
const openUpdateForm = () => {
  selectedTaskBoxResponse.value = taskBoxs.value.find(tb => tb.id.toString() === selectedTaskBox.value?.id) || null ;

  isEsTaskBoxFormOpened.value = true;
  headerEsTaskBoxForm.value = t('settings.label.task.taskbox.update.header');
  taskBoxs.value.find(tb => tb.id.toString() === selectedTaskBox.value?.id) || null;
};

/**
 * Opens the add form for creating a new task box.
 * Clears the current selectedTaskBoxResponse and
 * sets the modal header for add mode.
 */
const openAddForm = () => {
  selectedTaskBoxResponse.value = null;
  isEsTaskBoxFormOpened.value = true;
  headerEsTaskBoxForm.value = t('settings.label.task.taskbox.add.header');
};


/**
 * Closes the task box form modal and performs either an add or update
 * operation depending on whether a task box is selected.
 *
 * @param {AddOrUpdateFinancialChannelRequest} addOrUpdateFinancialChannelRequest - The request payload containing task box data.
 */
const closeEsTaskBoxForm = async (addOrUpdateFinancialChannelRequest: AddOrUpdateFinancialChannelRequest) => {
  if (addOrUpdateFinancialChannelRequest && currentPersonId.value) {
    if (selectedTaskBoxResponse.value) {
      await personApi.updateTaskBox({personId: currentPersonId.value, addOrUpdateTaskBoxRequest: addOrUpdateFinancialChannelRequest}).then(async () => {
          toastSuccess(toast, {
              summary: t('common.toast.success.add'),
              detail:  t('settings.label.task.taskbox.update.message.success')
          });
          await getTaskBoxs();
          isEsTaskBoxFormOpened.value = false;
        }).catch((error: ApiError) => {
          toastError(toast, {
              summary: t('common.toast.error'),
              detail: error.status ? t(`error.${error.status}`) : t('error.DEFAULT')
          });

          // Throw error to set at false the var __alreadyClicked in v-one-click directive
          throw error;
        });
    } else {
      await personApi.addTaskBox({personId: currentPersonId.value, addOrUpdateTaskBoxRequest: addOrUpdateFinancialChannelRequest}).then(async () => {
          toastSuccess(toast, {
              summary: t('common.toast.success.add'),
              detail:  t('settings.label.task.taskbox.add.message.success')
          });
          await getTaskBoxs();
          isEsTaskBoxFormOpened.value = false;
        }).catch((error: ApiError) => {
            toastError(toast, {
                summary: t('common.toast.error'),
                detail: error.status ? t(`error.${error.status}`) : t('error.DEFAULT')
            });
            // Throw error to set at false the var __alreadyClicked in v-one-click directive
            throw error;
        });
    }
  }
}

/**
 * Retrieves the permissions (assignees) for the currently selected task box
 * and updates the reactive `taskboxPermissions` state.
 */
const getTaskboxPermission = async () => {
  if(selectedTaskBox.value){
    loadingPermission.value = true;
    personApi.findPersonsByTaskBox({taskboxId: selectedTaskBox.value.id, instituteId: currInstituteUuid.value}).then((response: TaskBoxPersonsLightResponse[]) => {
      taskboxPermissions.value = response;
    }).catch((error: ApiError) => {
      toastError(toast, {
          summary: t('common.toast.error'),
          detail: error.status ? t('error.' + error.status) : t('common.toast.error')
      });
    }).finally(() => {
      loadingPermission.value = false;
    });
  }
}

/**
 * Handles toggling of task box permissions (assignee flag) for a given user.
 * Sends the updated state to the backend and updates the local permissions list.
 *
 * @async
 * @function
 * @param {TaskBoxPersonsLightResponse} taskboxPermission - The permission object for a specific person in the task box.
 * @returns {Promise<void>}
 */
const handlePermissionToggle = async (taskboxPermission: TaskBoxPersonsLightResponse) => {
    if (taskboxPermission && taskboxPermission.id && currInstituteUuid) {
      personApi.updateTaskboxPermission({instituteId: currInstituteUuid.value, taskboxId: selectedTaskBox.value.id, personId: taskboxPermission.id, updateTaskboxPermissionRequest: {assignee: taskboxPermission.isAssignee}}).then(async (result) => {
        if (result) {
          toastSuccess(toast, {
            summary: t('common.toast.success.update'),
            detail: t('settings.label.main.permissions.message.success.update')
          });
          taskboxPermissions.value = result;
        }
      }).catch((error: ApiError) => {
        taskboxPermission.isAssignee = false;
        toastError(toast, {
            summary: t('common.toast.error'),
            detail: error.status ? t('error.' + error.status) : t('common.toast.error')
        });
      });
    }
}

onMounted(async () => {
    await getTaskBoxs();
});

watch(
    () => selectedTaskBox.value,
  async () => {
    await getTaskboxPermission();
  }
);


return (_ctx: any,_cache: any) => {
  const _component_es_button_icon = _resolveComponent("es-button-icon")!
  const _component_es_button = _resolveComponent("es-button")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("span", _hoisted_3, _toDisplayString(_unref(t)('settings.label.task.taskbox.title')), 1),
        _createVNode(_unref(Select), {
          modelValue: selectedTaskBox.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedTaskBox).value = $event)),
          options: taskBoxsItemSelect.value,
          optionLabel: "label",
          size: "small",
          class: "w-full"
        }, {
          value: _withCtx((slotProps) => [
            (slotProps.value)
              ? (_openBlock(), _createElementBlock("img", {
                  key: 0,
                  src: _unref(SharedService).getIcon(slotProps.value.icon, false, slotProps.value.iconFolder)
                }, null, 8, _hoisted_4))
              : _createCommentVNode("", true),
            (slotProps.value)
              ? (_openBlock(), _createElementBlock("span", _hoisted_5, _toDisplayString(slotProps.value.label), 1))
              : _createCommentVNode("", true)
          ]),
          option: _withCtx((slotProps) => [
            _createElementVNode("img", {
              src: _unref(SharedService).getIcon(slotProps.option.icon, false, slotProps.option.iconFolder)
            }, null, 8, _hoisted_6),
            _createElementVNode("span", _hoisted_7, _toDisplayString(slotProps.option.label), 1)
          ]),
          _: 1
        }, 8, ["modelValue", "options"]),
        _createVNode(_component_es_button_icon, {
          class: "mright-05 pad-m",
          icon: "pencil_fill",
          folder: "shareIcons",
          styles: "img-grey",
          onClick: _cache[1] || (_cache[1] = ($event: any) => (openUpdateForm())),
          disabled: isUpdateDisabled.value
        }, null, 8, ["disabled"]),
        _createVNode(_component_es_button, {
          widthClass: "w-full",
          label: _unref(t)('action.add'),
          onClick: _cache[2] || (_cache[2] = ($event: any) => (openAddForm())),
          "is-primary": false,
          "is-linked": false
        }, null, 8, ["label"])
      ]),
      (loadingPermission.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_8, _toDisplayString(_unref(t)('settings.label.main.permissions.loading')), 1))
        : (taskboxPermissions.value && taskboxPermissions.value.length > 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(taskboxPermissions.value, (permission) => {
                return (_openBlock(), _createElementBlock("div", {
                  class: "row-space-between w-full es-main-settings mtop-10",
                  key: permission.id
                }, [
                  _createElementVNode("span", _hoisted_10, _toDisplayString(permission.firstname) + " " + _toDisplayString(permission.lastname), 1),
                  _createVNode(_unref(ToggleSwitch), {
                    modelValue: permission.isAssignee,
                    "onUpdate:modelValue": ($event: any) => ((permission.isAssignee) = $event),
                    onChange: () => handlePermissionToggle(permission)
                  }, null, 8, ["modelValue", "onUpdate:modelValue", "onChange"])
                ]))
              }), 128))
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_11, _toDisplayString(_unref(t)('settings.label.main.permissions.empty')), 1))
    ]),
    _createVNode(EsTaskBoxForm, {
      "is-open": isEsTaskBoxFormOpened.value,
      header: headerEsTaskBoxForm.value,
      taskBoxLightResponse: selectedTaskBoxResponse.value,
      "confirm-method": closeEsTaskBoxForm,
      onModalClose: _cache[3] || (_cache[3] = ($event: any) => (isEsTaskBoxFormOpened.value = false))
    }, null, 8, ["is-open", "header", "taskBoxLightResponse"])
  ], 64))
}
}

})